import { Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "src/redux/hooks";

import { HeaderBadge } from "../Header.styled";
import LogoutModal from "./LogoutModal";
import { refreshLogoutTimer } from "src/generator/utils/refreshLogoutTimer";

const LogoutTimer = () => {
  const [rerender, setRerender] = useState(0);
  const [timer, setTimer] = useState(0);
  const [opened, { open, close }] = useDisclosure();
  const logoutTime = useSelector(state => state.preferencesSlice.logoutTime);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleFocus = () => {
      setRerender(prev => prev + 1);
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  useEffect(() => {
    if (opened) return;

    let time = logoutTime;
    const start = localStorage.getItem("logoutModalStart");

    if (start) {
      const duration = Number(start) + logoutTime - Date.now();
      time = duration > 0 ? duration : 0;
    } else {
      refreshLogoutTimer();
    }

    time = time - (time % 1000);

    if (time <= 0) {
      setTimer(0);
      open();
      return;
    }

    if (time > 1000) {
      setTimer(time);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setTimer(prev => {
          if (prev <= 0) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            open();
            return 0;
          } else {
            return prev - 1000;
          }
        });
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimer(0);
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "logoutModalStart") {
        console.log("Logout timer updated");
        setRerender(prev => prev + 1);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      if (opened) close();
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [close, logoutTime, open, opened, rerender]);

  const getTimer = () => {
    const totalSeconds = Math.floor(timer / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    let seconds: number | string = totalSeconds - minutes * 60;
    seconds = seconds < 10 ? "0" + seconds : seconds;
    return `${minutes}:${seconds}`;
  };

  const getColor = () => {
    const secondsLeft = Math.floor(timer / 1000);
    if (secondsLeft < 60) {
      return "red.9";
    } else if (secondsLeft < 60 * 5) {
      return "yellow.9";
    } else {
      return "green.9";
    }
  };

  return (
    <>
      <Tooltip label="Session Timeout" openDelay={300}>
        <HeaderBadge
          size="lg"
          variant="light"
          color={getColor()}
          my="auto"
          style={{ userSelect: "none" }}>
          {getTimer()}
        </HeaderBadge>
      </Tooltip>
      <LogoutModal opened={opened} close={close} />
    </>
  );
};

export default LogoutTimer;
